import {
  updateChannelSchema,
  type Channel,
  type ChannelPermission,
  type OverrideTarget,
  type PermissionOverride,
} from '@nexo/types';
import {
  Avatar,
  Button,
  Field,
  IconButton,
  Input,
  Modal,
  SegmentedControl,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronRight, Trash2, Users } from 'lucide-react';
import { useState, type ReactNode, type SubmitEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { errorMessage, validate } from '../../lib/forms';
import { keys } from '../../lib/queries';
import { OverrideEditor } from '../teams/PermissionEditor';
import { RoleChip } from '../teams/roles';
import { useTranslation } from 'react-i18next';

const overridesKey = (channelId: string) => ['channel', channelId, 'permissions'] as const;

/** Channel settings: name and description, permission overrides and deletion. */
export function ChannelSettingsDialog({
  channel,
  open,
  onClose,
}: {
  channel: Channel;
  open: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'general' | 'permissions'>('general');
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Ajustes de #{{name}}', { name: channel.name })}
      size="md"
    >
      <SegmentedControl
        label={t('Sección')}
        value={tab}
        onChange={setTab}
        className="mb-4"
        options={[
          { value: 'general', label: t('General') },
          { value: 'permissions', label: t('Permisos') },
        ]}
      />
      {tab === 'general' ? (
        <GeneralSettings channel={channel} onClose={onClose} />
      ) : (
        <PermissionSettings channel={channel} />
      )}
    </Modal>
  );
}

function GeneralSettings({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const { team } = useTeam();
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description ?? '');
  const [error, setError] = useState<string>();
  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.channels(team.id) });

  const save = useMutation({
    mutationFn: () => api.channels.update(channel.id, { name, description: description || null }),
    onSuccess: () => {
      void refresh();
      toast({ title: t('Canal guardado'), tone: 'success' });
      onClose();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.channels.remove(channel.id),
    onSuccess: () => {
      void refresh();
      onClose();
      void navigate(`/app/t/${team.id}`);
    },
    onError: (err) =>
      toast({ title: t('No se pudo borrar'), description: errorMessage(err), tone: 'error' }),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { errors } = validate(updateChannelSchema, { name, description: description || null });
    if (errors) return setError(errors.name ?? errors.description);
    save.mutate();
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <Field label={t('Nombre')} error={error} hint={t('Minúsculas, números y guiones')}>
        {(props) => (
          <Input
            {...props}
            value={name}
            maxLength={40}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
          />
        )}
      </Field>
      <Field label={t('Descripción')}>
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            maxLength={250}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </Field>
      <div className="flex items-center gap-2 border-t border-border pt-4">
        <Button
          variant="ghost"
          className="text-danger"
          icon={<Trash2 className="size-4" />}
          loading={remove.isPending}
          onClick={() => {
            if (
              window.confirm(
                t(
                  '¿Borrar #{{name}}? Se borrarán todos sus mensajes y archivos. No se puede deshacer.',
                  { name: channel.name },
                ),
              )
            )
              remove.mutate();
          }}
        >
          {t('Borrar canal')}
        </Button>
        <span className="flex-1" />
        <Button type="submit" loading={save.isPending}>
          {t('Guardar')}
        </Button>
      </div>
    </form>
  );
}

interface Target {
  type: OverrideTarget;
  id: string;
}

function PermissionSettings({ channel }: { channel: Channel }) {
  const { team, roles, members, roleById, memberById } = useTeam();
  const overrides = useQuery({
    queryKey: overridesKey(channel.id),
    queryFn: () => api.channels.overrides(channel.id),
  });
  const [editing, setEditing] = useState<Target | null>(null);
  const [adding, setAdding] = useState('');
  const { t } = useTranslation();

  if (overrides.isPending) return <Spinner />;
  const list = overrides.data ?? [];
  const find = (t: Target) => list.find((o) => o.targetType === t.type && o.targetId === t.id);

  const label = (target: Target): ReactNode => {
    if (target.type === 'everyone') {
      return (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium">
          <Users className="size-4 text-muted" /> {t('Todos')}
        </span>
      );
    }
    if (target.type === 'role') {
      const role = roleById(target.id);
      return role ? <RoleChip role={role} className="text-xs" /> : t('Rol borrado');
    }
    const member = memberById(target.id);
    return member ? (
      <span className="inline-flex items-center gap-2 text-sm font-medium">
        <Avatar id={member.id} name={member.displayName} src={member.avatarUrl} size="xs" />
        {member.displayName}
      </span>
    ) : (
      t('Persona que ya no está')
    );
  };

  if (editing) {
    return (
      <OverrideForm
        channel={channel}
        target={editing}
        title={label(editing)}
        current={find(editing)}
        onBack={() => setEditing(null)}
      />
    );
  }

  // «Todos» always first, then roles and people with their own rules.
  const targets: Target[] = [
    { type: 'everyone', id: team.id },
    ...list
      .filter((o) => o.targetType !== 'everyone')
      .map((o) => ({ type: o.targetType, id: o.targetId })),
  ];
  const summary = (target: Target) => {
    const o = find(target);
    if (!o) return t('Sin cambios: usa los permisos de sus roles');
    const parts = [
      o.allow.length > 0 &&
        (o.allow.length === 1
          ? t('1 permitido')
          : t('{{count}} permitidos', { count: o.allow.length })),
      o.deny.length > 0 &&
        (o.deny.length === 1
          ? t('1 denegado')
          : t('{{count}} denegados', { count: o.deny.length })),
    ].filter(Boolean);
    return parts.join(' · ');
  };
  const available = [
    ...roles
      .filter((r) => !find({ type: 'role', id: r.id }))
      .map((r) => ({ value: `role:${r.id}`, label: t('Rol · {{name}}', { name: r.name }) })),
    ...members
      .filter((m) => m.role === 'member' && !find({ type: 'member', id: m.id }))
      .map((m) => ({
        value: `member:${m.id}`,
        label: t('Persona · {{name}}', { name: m.displayName }),
      })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted">
        {t(
          'Ajusta lo que cada rol o persona puede hacer en este canal. Por ejemplo, deniega «Ver el canal» a Todos y permítelo a un rol para que solo ese rol lo vea. Los administradores siempre lo ven todo.',
        )}
      </p>
      <ul className="flex flex-col divide-y divide-border rounded-xl border border-border">
        {targets.map((t) => (
          <li key={`${t.type}:${t.id}`}>
            <button
              type="button"
              onClick={() => setEditing(t)}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-surface-2"
            >
              <span className="min-w-0 flex-1">
                {label(t)}
                <span className="mt-1 block text-xs text-muted">{summary(t)}</span>
              </span>
              <ChevronRight className="size-4 text-subtle" />
            </button>
          </li>
        ))}
      </ul>
      {available.length > 0 && (
        <div className="flex items-end gap-2">
          <Field label={t('Añadir un rol o una persona')} className="flex-1">
            {(props) => (
              <Select
                {...props}
                value={adding}
                onChange={setAdding}
                placeholder={t('Elige…')}
                options={available}
              />
            )}
          </Field>
          <Button
            variant="secondary"
            disabled={!adding}
            onClick={() => {
              const [type, id] = adding.split(':') as [OverrideTarget, string];
              setAdding('');
              setEditing({ type, id });
            }}
          >
            {t('Añadir')}
          </Button>
        </div>
      )}
    </div>
  );
}

function OverrideForm({
  channel,
  target,
  title,
  current,
  onBack,
}: {
  channel: Channel;
  target: Target;
  title: ReactNode;
  current: PermissionOverride | undefined;
  onBack: () => void;
}) {
  const { team } = useTeam();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [value, setValue] = useState<{ allow: ChannelPermission[]; deny: ChannelPermission[] }>({
    allow: current?.allow ?? [],
    deny: current?.deny ?? [],
  });
  const save = useMutation({
    mutationFn: (next: typeof value) =>
      api.channels.saveOverride(channel.id, {
        targetType: target.type,
        targetId: target.id,
        ...next,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: overridesKey(channel.id) });
      void queryClient.invalidateQueries({ queryKey: keys.channels(team.id) });
      onBack();
    },
    onError: (err) =>
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <IconButton label={t('Volver')} size="sm" onClick={onBack}>
          <ArrowLeft className="size-4" />
        </IconButton>
        <div className="min-w-0 flex-1 truncate">{title}</div>
      </div>
      <OverrideEditor
        allow={value.allow}
        deny={value.deny}
        onChange={setValue}
        grantable={channel.permissions}
      />
      <div className="sticky bottom-0 mt-4 flex items-center gap-2 border-t border-border bg-surface pt-3">
        {current && target.type !== 'everyone' && (
          <Button
            variant="ghost"
            className="text-danger"
            icon={<Trash2 className="size-4" />}
            onClick={() => save.mutate({ allow: [], deny: [] })}
          >
            {t('Quitar')}
          </Button>
        )}
        <span className="flex-1" />
        <Button variant="ghost" onClick={onBack}>
          {t('Cancelar')}
        </Button>
        <Button loading={save.isPending} onClick={() => save.mutate(value)}>
          {t('Guardar')}
        </Button>
      </div>
    </div>
  );
}
